// @djsem/ui — AI Stem Separation Studio UX model (Canonical Prompt 135/19A, extended by
// 136 review states, 140/141 model registry).
//
// Pure, framework-free logic backing the Stem Studio screens: separation-mode options,
// source eligibility gating (blocks platform-metadata-only sources per the hard rule),
// active rights/credit confirmation (no pre-ticked checkbox), job status → UI copy, and
// stem quality in plain language (prompt 136 §6). React renders this; the rules stay
// unit-testable without a DOM.

/** A stem separation mode offered to the user. */
export type DJSEMStemSeparationModeUI =
  | "two_stem_vocals_instrumental"
  | "four_stem_drums_bass_vocals_other"
  | "five_stem_drums_bass_vocals_melody_other"
  | "custom_provider";

/** A selectable stem-mode option with its outputs and best-use description. */
export interface StemModeOption {
  mode: DJSEMStemSeparationModeUI;
  label: string;
  outputs: string[];
  bestFor: string;
}

/** Required separation modes (prompt 135 §7). */
export const STEM_MODE_OPTIONS: readonly StemModeOption[] = [
  { mode: "two_stem_vocals_instrumental", label: "2-stem", outputs: ["vocals", "instrumental"], bestFor: "Acapella/instrumental split, quick remix prep" },
  { mode: "four_stem_drums_bass_vocals_other", label: "4-stem", outputs: ["drums", "bass", "vocals", "other"], bestFor: "Standard set-prep and remix workflows" },
  { mode: "five_stem_drums_bass_vocals_melody_other", label: "5-stem", outputs: ["drums", "bass", "vocals", "melody", "other"], bestFor: "Melody-aware transitions and visual mapping" },
  { mode: "custom_provider", label: "Advanced (provider-defined)", outputs: ["varies by provider"], bestFor: "Power users on an approved advanced model" },
] as const;

/**
 * Look up a stem mode's option details.
 * @param mode - The mode to describe.
 * @returns The matching {@link StemModeOption}, or undefined if unknown.
 */
export function describeStemMode(mode: DJSEMStemSeparationModeUI): StemModeOption | undefined {
  return STEM_MODE_OPTIONS.find((m) => m.mode === mode);
}

// ---- source eligibility (prompt 135 §6) -------------------------------------

/** Whether/why a source may (or may not) be run through stem separation. */
export type StemSourceEligibility = "eligible" | "missing_file" | "platform_metadata_only" | "unsupported_format" | "rights_blocked";

/** Inputs needed to check a source's stem-separation eligibility. */
export interface StemSourceCheckInput {
  hasLocalFile: boolean;
  sourcePlatform?: "spotify" | "soundcloud" | "youtube" | "local" | "upload";
  fileFormatSupported: boolean;
  rightsState: "unknown" | "owned" | "licensed" | "restricted" | "ai_generated_uncleared";
}

/** Result of a stem-separation source eligibility check. */
export interface StemSourceCheckResult {
  eligibility: StemSourceEligibility;
  reason: string;
}

/**
 * Source eligibility (prompt 135 §6/§9) — blocks metadata-only platform references from
 * separation. This is a UI-side pre-check; the worker route + contract layer still enforce
 * the same rule server-side (fail closed either way).
 * @param input - The source's file/platform/rights state.
 * @returns The eligibility verdict and a human-readable reason.
 */
export function checkStemSourceEligibility(input: StemSourceCheckInput): StemSourceCheckResult {
  if (input.rightsState === "restricted") {
    return { eligibility: "rights_blocked", reason: "This source is marked restricted — separation is blocked until rights are cleared." };
  }
  const isStreamedPlatform = input.sourcePlatform === "spotify" || input.sourcePlatform === "soundcloud" || input.sourcePlatform === "youtube";
  if (isStreamedPlatform && !input.hasLocalFile) {
    return {
      eligibility: "platform_metadata_only",
      reason: `${input.sourcePlatform} references are metadata-only — DJSEM cannot separate streamed audio. Supply a local/licensed file to enable Stem Studio.`,
    };
  }
  if (!input.hasLocalFile) {
    return { eligibility: "missing_file", reason: "No local audio file found for this track — locate or re-import the file to run stem separation." };
  }
  if (!input.fileFormatSupported) {
    return { eligibility: "unsupported_format", reason: "This file format isn't supported for stem separation yet." };
  }
  return { eligibility: "eligible", reason: "Ready — this source can be separated." };
}

/**
 * Stem Studio is desktop-only regardless of plan (ARTIST_IDE_GUARDRAIL_V3.md §4/§5 — "Desktop
 * app = everything in web plus... stems"; §5's desktop/pro-only list). Pure so the module-parity
 * audit (which modules are web-available) is unit-testable without rendering anything.
 * @param platformKind - The current host.
 * @returns Availability and, when unavailable, a reason.
 */
export function stemStudioAvailability(platformKind: "web" | "electron"): { available: boolean; reason?: string } {
  if (platformKind === "web") {
    return { available: false, reason: "Stem separation needs the desktop app's local processing path." };
  }
  return { available: true };
}

// ---- rights/credit confirmation (prompt 135 §8) -----------------------------

/** Active (never pre-ticked) rights + credit confirmation checkboxes for starting a stem job. */
export interface StemRightsConfirmationState {
  rightsChecked: boolean;
  creditChecked: boolean;
}

/**
 * No job may be created until BOTH boxes are actively checked — no pre-ticked checkbox.
 * @param state - Current confirmation checkbox state.
 * @returns True only if both boxes are checked.
 */
export function canCreateStemJob(state: StemRightsConfirmationState): boolean {
  return state.rightsChecked && state.creditChecked;
}

/** Canonical rights/credit confirmation copy shown before creating a stem separation job. */
export const STEM_RIGHTS_CONFIRMATION_COPY = [
  "Only separate audio you own or have permission to use.",
  "Do not use DJSEM to rip or separate Spotify, SoundCloud, YouTube, or other streamed audio.",
  "Generated stems may be restricted from export, release, marketplace, or public use depending on rights.",
  "DJSEM records rights confirmation and provenance for generated stems.",
] as const;

// ---- job status → UI copy (prompt 135 §5/§11) -------------------------------

/** Lifecycle status of a stem separation job. */
export type StemJobStatusUI =
  | "created" | "awaiting_rights_confirmation" | "awaiting_credits"
  | "queued" | "running" | "completed" | "failed" | "cancelled" | "expired";

/**
 * Color tone for a stem job status badge.
 * @param status - The job's current status.
 * @returns The tone to render it with.
 */
export function stemJobStatusTone(status: StemJobStatusUI): "neutral" | "ok" | "warn" | "danger" {
  switch (status) {
    case "completed": return "ok";
    case "failed": case "expired": return "danger";
    case "awaiting_rights_confirmation": case "awaiting_credits": case "cancelled": return "warn";
    default: return "neutral";
  }
}

/**
 * Human-readable label for a stem job status.
 * @param status - The job's current status.
 * @returns The display label.
 */
export function stemJobStatusLabel(status: StemJobStatusUI): string {
  switch (status) {
    case "created": return "Created";
    case "awaiting_rights_confirmation": return "Awaiting rights confirmation";
    case "awaiting_credits": return "Awaiting credit confirmation";
    case "queued": return "Queued";
    case "running": return "Running";
    case "completed": return "Review required";
    case "failed": return "Failed";
    case "cancelled": return "Cancelled";
    case "expired": return "Expired";
    default: return status;
  }
}

// ---- stem quality in plain language (prompt 136 §6) -------------------------

/** Plain-language stem output quality rating. */
export type StemQualityLabel = "Excellent" | "Good" | "Usable with warnings" | "Poor" | "Failed" | "Unknown";

/**
 * Plain-language quality is required; numeric confidence is optional/internal (prompt 136 §6).
 * @param confidence - Optional numeric confidence, 0..1.
 * @param hasWarnings - Whether the output carries any quality warnings.
 * @returns The plain-language {@link StemQualityLabel}.
 */
export function stemQualityLabel(confidence: number | undefined, hasWarnings: boolean): StemQualityLabel {
  if (confidence === undefined) return "Unknown";
  if (confidence <= 0) return "Failed";
  if (confidence >= 0.85) return hasWarnings ? "Usable with warnings" : "Excellent";
  if (confidence >= 0.7) return hasWarnings ? "Usable with warnings" : "Good";
  if (confidence >= 0.5) return "Usable with warnings";
  return "Poor";
}

// ---- track-level stem status (prompt 136 §7) --------------------------------
// Richer than the Library row's compact 3-state `stemStatus` summary — this drives the
// Stem Studio / Review screens specifically.

/** Track-level stem status summary (drives Stem Studio / Review screens). */
export type TrackStemStatusUI =
  | "not_available" | "queued" | "running" | "review_required"
  | "approved" | "partially_approved" | "rejected" | "failed" | "archived";

/** Per-track counts of approved/rejected/pending-review stem outputs. */
export interface StemApprovalCounts { approved: number; rejected: number; pendingReview: number; }

/**
 * Derive a track's overall stem status from its job status and per-output approval counts.
 * @param jobStatus - The track's most recent separation job status, or `"none"`.
 * @param approvals - Approval counts for the track's stem outputs.
 * @returns The derived {@link TrackStemStatusUI}.
 */
export function deriveTrackStemStatus(jobStatus: StemJobStatusUI | "none", approvals: StemApprovalCounts): TrackStemStatusUI {
  if (jobStatus === "none" || jobStatus === "cancelled") return "not_available";
  if (jobStatus === "created" || jobStatus === "awaiting_rights_confirmation" || jobStatus === "awaiting_credits" || jobStatus === "queued") return "queued";
  if (jobStatus === "running") return "running";
  if (jobStatus === "failed" || jobStatus === "expired") return "failed";
  // completed:
  if (approvals.approved > 0 && approvals.rejected > 0) return "partially_approved";
  if (approvals.approved > 0) return "approved";
  if (approvals.rejected > 0 && approvals.pendingReview === 0) return "rejected";
  return "review_required";
}

// ---- per-stem approval state (prompt 136 §7) --------------------------------

/** Per-stem-output approval state. */
export type StemApprovalStateUI = "pending_review" | "approved" | "rejected" | "needs_regeneration" | "archived" | "rights_blocked";

/**
 * Color tone for a stem approval-state badge.
 * @param state - The output's current approval state.
 * @returns The tone to render it with.
 */
export function stemApprovalTone(state: StemApprovalStateUI): "neutral" | "ok" | "warn" | "danger" {
  switch (state) {
    case "approved": return "ok";
    case "rejected": case "rights_blocked": return "danger";
    case "needs_regeneration": return "warn";
    default: return "neutral";
  }
}

/**
 * Human-readable label for a stem approval state.
 * @param state - The output's current approval state.
 * @returns The display label.
 */
export function stemApprovalLabel(state: StemApprovalStateUI): string {
  switch (state) {
    case "pending_review": return "Pending review";
    case "approved": return "Approved";
    case "rejected": return "Rejected";
    case "needs_regeneration": return "Needs regeneration";
    case "archived": return "Archived";
    case "rights_blocked": return "Rights blocked";
    default: return state;
  }
}

/**
 * Actions offered per approval state (prompt 136 §8 — minimum review/editing actions).
 * @param state - The output's current approval state.
 * @returns The offered actions for this state.
 */
export function stemApprovalActionsFor(state: StemApprovalStateUI): Array<"approve" | "reject" | "regenerate" | "archive" | "restore"> {
  switch (state) {
    case "pending_review": return ["approve", "reject", "regenerate"];
    case "rejected": case "needs_regeneration": return ["regenerate", "archive"];
    case "approved": return ["reject", "archive"];
    case "archived": return ["restore"];
    default: return [];
  }
}

// ---- export type gate (prompt 139 §6/§7) ------------------------------------
// Mirrors the 8 export types in @djsem/shared's stemExportPolicy.ts — kept as a small local
// literal list (not imported) so this UI package doesn't take on a new cross-package
// dependency just for 8 string labels.

/** The 8 stem export types (mirrors @djsem/shared's stemExportPolicy.ts). */
export const STEM_EXPORT_TYPES = [
  "private_project_export", "set_prep_pack", "show_pack", "rehearsal_pack",
  "remix_prep_pack", "release_pack", "marketplace_pack", "public_campaign_reward",
] as const;
/** One of the {@link STEM_EXPORT_TYPES}. */
export type StemExportTypeUI = (typeof STEM_EXPORT_TYPES)[number];

/**
 * Human-readable label for a stem export type.
 * @param type - The export type.
 * @returns The display label (title-cased, underscores replaced with spaces).
 */
export function stemExportTypeLabel(type: StemExportTypeUI): string {
  return type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
